import { useMeterReadings } from "@features/bx/BulkBillGeneration/hooks";
import { GenericTable } from "@shared/components/GenericTable";
import { TableColumn } from "@shared/types/table";
import { PAGE_SIZE } from "@shared/utils/constants";
import { AlertTriangle, FileText } from "lucide-react";
import React, { useState } from "react";
import { useParams } from "react-router-dom";
import { MeterReadingFilters } from "./Filters/AllReadingFilters";

// Interface for High Consumption data
interface HighConsumptionReading {
  id: string | number;
  consumerNumber: string;
  consumerName: string;
  meterNo: string;
  meterType: string;
  service: string;
  lastReading: number | string;
  currentReading: number | string;
  consumption: string;
  avgConsumption: string;
  consumptionChangePercent: string;
}
interface MeterReadingFiltersType {
  veeStatus: string;
  status: string;
  meterType: string;
  service: string;
  page?: number;
  limit?: number;
  consumptionFlag?: string;
}
export const HighConsumptionTable: React.FC = () => {
  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");

  const pageSize = PAGE_SIZE;
  const { id, cycleId, action } = useParams();

  const [filters, setFilters] = useState<MeterReadingFiltersType>({
    veeStatus: "all",
    status: "all",
    meterType: "all",
    service: "all",
    page: currentPage,
    limit: pageSize,
    consumptionFlag: "high",
  });

  const formatFiltersForHook = (
    filters: MeterReadingFiltersType
  ): { page: number; limit: number; search?: string; [key: string]: any } => {
    const hookFilters: {
      page: number;
      limit: number;
      search?: string;
      [key: string]: any;
    } = {
      page: currentPage,
      limit: PAGE_SIZE,
    };

    const filterKeys: (keyof MeterReadingFiltersType)[] = [
      "veeStatus",
      "status",
      "meterType",
      "service",
    ];

    // Add search term
    if (searchTerm.trim()) {
      hookFilters.search = searchTerm;
    }
    filterKeys.forEach((key) => {
      const value = filters[key];
      if (value && value !== "all") {
        hookFilters[key] = value;
      }
    });

    if (filters.consumptionFlag) {
      hookFilters.consumptionFlag = filters.consumptionFlag;
    }

    return hookFilters;
  };

  const { data: meterReadings, isLoading } = useMeterReadings(cycleId as string, id as string, formatFiltersForHook(filters));

  // Define table columns
  const columns: TableColumn<HighConsumptionReading>[] = [
    {
      key: "consumerNumber",
      header: "Consumer Number",
      type: "text",
      sortable: false,
    },
    {
      key: "consumerName",
      header: "Consumer Name",
      type: "text",
      sortable: false,
    },
    {
      key: "meterNo",
      header: "Resource ID",
      type: "text",
      sortable: false,
    },
    {
      key: "meterType",
      header: "Resource Type",
      type: "text",
      sortable: false,
    },
    {
      key: "service",
      header: "Service",
      type: "text",
      sortable: false,
    },
    {
      key: "lastReading",
      header: "Last Reading",
      type: "text",
      sortable: false,
    },
    {
      key: "currentReading",
      header: "Current Reading",
      type: "text",
      sortable: false,
    },
    {
      key: "consumption",
      header: "Consumption",
      type: "text",
      sortable: false,
    },
    {
      key: "avgConsumption",
      header: "Avg Consumption",
      type: "text",
      sortable: false,
    },
    {
      key: "consumptionChangePercentage",
      header: "Consumption Change %",
      render: (value: string) => (
        <span className="text-red-600 font-medium">{value}</span>
      ),
    },
  ];

  const handleSearch = (searchValue: string, paramName: string) => {
    setSearchTerm(searchValue);
    setCurrentPage(1);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const totalItems = meterReadings?.count || 0;
  const totalPages = Math.ceil(totalItems / pageSize);
  const startItem = (currentPage - 1) * pageSize + 1;
  const endItem = Math.min(currentPage * pageSize, totalItems);
  const handleFiltersChange = (newFilters: MeterReadingFiltersType) => {
    setFilters(newFilters);
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setFilters({
      veeStatus: "all",
      status: "all",
      meterType: "all",
      service: "all",
    });
    setSearchTerm("");
    setCurrentPage(1);
  };
  return (
    <div className="space-y-4">
      {/* Warning Banner */}
      <div className="flex items-start space-x-3 p-4 bg-red-50 border border-red-200 rounded-lg">
        <AlertTriangle
          className="text-red-600 mt-0.5 flex-shrink-0"
          size={20}
        />
        <p className="text-sm text-red-800 flex-1">
          These consumers show consumption more than 60% above their average.
          Verify meter accuracy or investigate potential issues before billing.
        </p>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">
              High Consumption
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              consumers with consumption more than 60% of avg consumption
            </p>
          </div>
        </div>

        {/* Generic Table */}
        <GenericTable
          data={meterReadings?.results || []}
          columns={columns}
          rowKey="id"
          onSearch={handleSearch}
          filterPanel={
            <MeterReadingFilters
              filters={filters}
              onFiltersChange={handleFiltersChange}
              onApplyFilters={handleFiltersChange}
              onClearFilters={clearFilters}
            />
          }
          pagination={{
            currentPage,
            totalPages,
            totalItems,
            itemsPerPage: PAGE_SIZE,
            startItem,
            endItem,
          }}
          onPageChange={handlePageChange}
          emptyMessage="No high consumption readings found"
          emptyIcon={<FileText className="h-8 w-8 text-gray-400" />}
        />
      </div>
    </div>
  );
};
